import React, { useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import {
  Genre,
  Momentum,
  Peakairplay,
  Region,
  Rotation,
} from 'src/assets/customIcons';
import { useSelector } from 'react-redux';
import { RootState } from 'src/app/store';
import {
  InsightsContainerMobile,
  InsightsCardMobile,
  InsightsWrapperMobile,
  CardTypeWrapper,
  CardType,
  CardStatus,
  ViewInsightButton,
  CardSpacingMobile,
} from './SmartInsights.styles';
import { getMomentumCardContent } from './SmartInsightCardsContent/MomentumSmartInsight';
import { getPeakAirplayCardContent } from './SmartInsightCardsContent/PeakAirplaySmartInsights';
import { getRotationCardContent } from './SmartInsightCardsContent/RotationCardContent';
import { getRegionCardContent } from './SmartInsightCardsContent/RegionCardContent';
import { getGenreCardContent } from './SmartInsightCardsContent/GenreCardContent';
import {
  hasMorningInsight,
  hasEveningInsight,
  hasNightInsight,
  hasMomentumInsight,
  hasRotationInsight,
  hasRegionsInsight,
  hasGenresInsight,
} from './SmartInsightsCardContentHelper';

const SmartInsightsMobile: React.FC = () => {
  const { t } = useTranslation();
  const [isMomentumActive, setMomentumActive] = useState(false);
  const [isPeakActiveMorning, setPeakActiveMorning] = useState(false);
  const [isPeakActiveEvening, setPeakActiveEvening] = useState(false);
  const [isPeakActiveNight, setPeakActiveNight] = useState(false);
  const [isRotationActive, setRotationActive] = useState(false);
  const [isRegionActive, setRegionActive] = useState(false);
  const [isGenreActive, setGenreActive] = useState(false);

  const { songInsights } = useSelector((state: RootState) => state.dashboard);

  return (
    <>
      {/* Smart insights - mobile (display order: Momentum, Peak Airplay, Rotation, Region, Genre) */}
      <InsightsWrapperMobile>
        {/* ---- 1. Momentum ---- */}
        {hasMomentumInsight(songInsights?.songInsightsData) && (
          <InsightsContainerMobile>
            <InsightsCardMobile $active={isMomentumActive}>
              <CardTypeWrapper>
                <Momentum />
                <CardType>
                  <Trans i18nKey="MOMENTUM_LABEL" />
                </CardType>
              </CardTypeWrapper>
              <CardStatus>
                {getMomentumCardContent(songInsights, isMomentumActive)}
              </CardStatus>
              <CardSpacingMobile />

              {/* View insight button */}
              <ViewInsightButton
                onClick={() => setMomentumActive((prev) => !prev)}
                aria-pressed={isMomentumActive}
              >
                {isMomentumActive
                  ? t('VIEW_INSIGHT_CTA')
                  : t('VIEW_RECOMMENDATION_CTA')}
              </ViewInsightButton>
            </InsightsCardMobile>
          </InsightsContainerMobile>
        )}

        {/* ---- 2. Peak Airplay (Morning) ---- */}
        {hasMorningInsight(songInsights?.songInsightsData) && (
          <InsightsContainerMobile>
            <InsightsCardMobile $active={isPeakActiveMorning}>
              <CardTypeWrapper>
                <Peakairplay />
                <CardType>
                  <Trans i18nKey="PEAK_AIRPLAY_LABEL" />
                </CardType>
              </CardTypeWrapper>
              <CardStatus>
                {getPeakAirplayCardContent(
                  songInsights,
                  'MORNING',
                  isPeakActiveMorning
                )}
              </CardStatus>
              <CardSpacingMobile />

              {/* View insight button */}
              <ViewInsightButton
                onClick={() => setPeakActiveMorning((prev) => !prev)}
                aria-pressed={isPeakActiveMorning}
              >
                {isPeakActiveMorning
                  ? t('VIEW_INSIGHT_CTA')
                  : t('VIEW_RECOMMENDATION_CTA')}
              </ViewInsightButton>
            </InsightsCardMobile>
          </InsightsContainerMobile>
        )}

        {/* ---- 2. Peak Airplay (Evening) ---- */}
        {hasEveningInsight(songInsights?.songInsightsData) && (
          <InsightsContainerMobile>
            <InsightsCardMobile $active={isPeakActiveEvening}>
              <CardTypeWrapper>
                <Peakairplay />
                <CardType>
                  <Trans i18nKey="PEAK_AIRPLAY_LABEL" />
                </CardType>
              </CardTypeWrapper>
              <CardStatus>
                {getPeakAirplayCardContent(
                  songInsights,
                  'EVENING',
                  isPeakActiveEvening
                )}
              </CardStatus>
              <CardSpacingMobile />

              {/* View insight button */}
              <ViewInsightButton
                onClick={() => setPeakActiveEvening((prev) => !prev)}
                aria-pressed={isPeakActiveEvening}
              >
                {isPeakActiveEvening
                  ? t('VIEW_INSIGHT_CTA')
                  : t('VIEW_RECOMMENDATION_CTA')}
              </ViewInsightButton>
            </InsightsCardMobile>
          </InsightsContainerMobile>
        )}

        {/* ---- 2. Peak Airplay (Night) ---- */}
        {hasNightInsight(songInsights?.songInsightsData) && (
          <InsightsContainerMobile>
            <InsightsCardMobile $active={isPeakActiveNight}>
              <CardTypeWrapper>
                <Peakairplay />
                <CardType>
                  <Trans i18nKey="PEAK_AIRPLAY_LABEL" />
                </CardType>
              </CardTypeWrapper>
              <CardStatus>
                {getPeakAirplayCardContent(
                  songInsights,
                  'NIGHT',
                  isPeakActiveNight
                )}
              </CardStatus>
              <CardSpacingMobile />

              {/* View insight button */}
              <ViewInsightButton
                onClick={() => setPeakActiveNight((prev) => !prev)}
                aria-pressed={isPeakActiveNight}
              >
                {isPeakActiveNight
                  ? t('VIEW_INSIGHT_CTA')
                  : t('VIEW_RECOMMENDATION_CTA')}
              </ViewInsightButton>
            </InsightsCardMobile>
          </InsightsContainerMobile>
        )}

        {/* ---- 3. Rotation ---- */}
        {hasRotationInsight(songInsights?.songInsightsData) && (
          <InsightsContainerMobile>
            <InsightsCardMobile $active={isRotationActive}>
              <CardTypeWrapper>
                <Rotation />
                <CardType>
                  <Trans i18nKey="ROTATION_LABEL" />
                </CardType>
              </CardTypeWrapper>

              <CardStatus>
                {getRotationCardContent(songInsights, isRotationActive)}
              </CardStatus>
              <CardSpacingMobile />

              {/* View insight button */}
              <ViewInsightButton
                onClick={() => setRotationActive((prev) => !prev)}
                aria-pressed={isRotationActive}
              >
                {isRotationActive
                  ? t('VIEW_INSIGHT_CTA')
                  : t('VIEW_RECOMMENDATION_CTA')}
              </ViewInsightButton>
            </InsightsCardMobile>
          </InsightsContainerMobile>
        )}

        {/* ---- 4. Region ---- */}
        {hasRegionsInsight(songInsights?.songInsightsData) && (
          <InsightsContainerMobile>
            <InsightsCardMobile $active={isRegionActive}>
              <CardTypeWrapper>
                <Region />
                <CardType>
                  <Trans i18nKey="REGION_LABEL" />
                </CardType>
              </CardTypeWrapper>
              <CardStatus>
                {getRegionCardContent(songInsights, isRegionActive)}
              </CardStatus>
              <CardSpacingMobile />

              {/* View insight button */}
              <ViewInsightButton
                onClick={() => setRegionActive((prev) => !prev)}
                aria-pressed={isRegionActive}
              >
                {isRegionActive
                  ? t('VIEW_INSIGHT_CTA')
                  : t('VIEW_RECOMMENDATION_CTA')}
              </ViewInsightButton>
            </InsightsCardMobile>
          </InsightsContainerMobile>
        )}

        {/* ---- 5. Genre ---- */}
        {hasGenresInsight(songInsights?.songInsightsData) && (
          <InsightsContainerMobile>
            <InsightsCardMobile $active={isGenreActive}>
              <CardTypeWrapper>
                <Genre />
                <CardType>
                  <Trans i18nKey="GENRE_LABEL" />
                </CardType>
              </CardTypeWrapper>
              <CardStatus>
                {getGenreCardContent(songInsights, isGenreActive)}
              </CardStatus>

              {/* View insight button */}
              <ViewInsightButton
                onClick={() => setGenreActive((prev) => !prev)}
                aria-pressed={isGenreActive}
              >
                {isGenreActive
                  ? t('VIEW_INSIGHT_CTA')
                  : t('VIEW_RECOMMENDATION_CTA')}
              </ViewInsightButton>
            </InsightsCardMobile>
          </InsightsContainerMobile>
        )}
      </InsightsWrapperMobile>
    </>
  );
};

export default SmartInsightsMobile;
